import type { ReactElement } from 'react';
import { Badge } from '@/components/atoms/badge';
import { MasteryMeter } from '@/components/molecules/mastery-meter';
import type { MasteryVisualLevel } from '@/lib/mastery';
import { cn } from '@/lib/cn';

export type PieceRowProps = {
  title: string;
  composer: string;
  form: string;
  musicalKey: string;
  mastery: MasteryVisualLevel;
  lastPracticed?: string;
  dueNow?: boolean;
  active?: boolean;
  tags?: readonly string[];
  compact?: boolean;
  onClick?: () => void;
};

export function PieceRow({
  title, composer, form, musicalKey, mastery, lastPracticed,
  dueNow = false, active = false, tags = [], compact = false, onClick,
}: PieceRowProps): ReactElement {
  return (
    <div
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 p-3 rounded-[var(--r-md)] border transition-colors',
        active
          ? 'bg-[var(--ink)] border-[var(--ink)] text-[var(--bg-surface)]'
          : 'bg-[var(--bg-surface)] border-[var(--rule)] hover:border-[var(--rule-strong)] cursor-pointer',
      )}
    >
      <div
        className={cn(
          'w-10 h-10 rounded-[var(--r-sm)] flex items-center justify-center font-display font-semibold text-[16px] shrink-0',
          active ? 'bg-white/10 text-white' : 'bg-[var(--bg-soft)] text-[var(--ink-2)]',
        )}
        aria-hidden
      >
        {title[0]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'font-display text-[15px] font-semibold truncate',
              active ? 'text-white' : 'text-[var(--ink)]',
            )}
          >
            {title}
          </div>
          {dueNow && !active && <Badge tone="warm" size="sm">Due</Badge>}
        </div>
        <div
          className={cn(
            'flex items-center gap-1.5 text-[11px] mt-0.5',
            active ? 'text-white/60' : 'text-[var(--ink-3)]',
          )}
        >
          <span>{form}</span><span>·</span>
          <span className="font-mono">{musicalKey}</span><span>·</span>
          <span className="truncate">{composer}</span>
        </div>
        {!compact && tags.length > 0 && (
          <div className="flex items-center gap-1 mt-1.5">
            {tags.map((t) => (
              <span
                key={t}
                className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded',
                  active ? 'bg-white/10 text-white/80' : 'bg-[var(--bg-soft)] text-[var(--ink-3)]',
                )}
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <MasteryMeter level={mastery} showLabel={false} size="sm" />
        {lastPracticed && (
          <span
            className={cn(
              'text-[10px]',
              active ? 'text-white/50' : 'text-[var(--ink-3)]',
            )}
          >
            {lastPracticed}
          </span>
        )}
      </div>
    </div>
  );
}
